/**
 * The chat filter (ui-map-interface.md §8 "The chat filter", R2937).
 *
 * A store app must filter what players write (Apple 1.2). The filter masks
 * each word of the locale's list with `•`, one mark for each character,
 * on the side of the reader: the line on the wire and on the server stays
 * as it was written. A word matches as a whole word, in any case, after
 * the folding of the common digit and sign swaps (0 1 3 4 5 @ $ to
 * o i e a s a s). A longer word that holds a listed word does not match.
 *
 * The SOCIAL switch CHAT FILTER turns it on and off. Its default follows
 * the platform: ON in a store app, OFF in a browser (`chatFilterOn`).
 *
 * The list compiles once, at the first use. The mask of a line runs once:
 * the state notes each line when it arrives (`noteChatLine`), and the
 * draw reads the kept mask (`chatText`), so no frame pays for it.
 */

import { chatfilter as S } from '../gen/strings';
import { storeApp, type StoreApp } from '../platform/platform';
import { storedReader } from './localstore';
import { parseSettings, type GameSettings } from './settings';
import { SETTINGS_KEY } from './settingskey';
import { runeCount } from '../util/text';

/** The mark that stands for each character of a masked word. */
export const MASK = '•';

/** The digit and sign swaps the filter folds before it compares. */
const FOLD: Readonly<Record<string, string>> = {
  0: 'o', 1: 'i', 3: 'e', 4: 'a', 5: 's', '@': 'a', $: 's',
};

/** One word of a chat line: letters, digits, `@` and `$`. Every other
 *  character ends a word. */
const WORD = /[\p{L}\p{N}@$]+/gu;

/** A word in the form the filter compares: lower case, swaps folded. */
export function foldWord(word: string): string {
  let out = '';
  for (const ch of word.toLowerCase()) out += FOLD[ch] ?? ch;
  return out;
}

/** The set of folded words of a list of words that spaces divide. */
export function compileWords(list: string): ReadonlySet<string> {
  return new Set(list.split(/\s+/).filter((w) => w !== '').map(foldWord));
}

/** `text` with each listed word masked, one mark for each character. */
export function maskText(text: string, words: ReadonlySet<string>): string {
  if (words.size === 0 || text === '') return text;
  return text.replace(WORD, (w) => (words.has(foldWord(w)) ? MASK.repeat(runeCount(w)) : w));
}

let compiled: ReadonlySet<string> | null = null;

/** The word list of the picked language, compiled once. */
function listWords(): ReadonlySet<string> {
  compiled ??= compileWords(S.words);
  return compiled;
}

/** The masked text of each line, kept by the line object. */
const masked = new WeakMap<object, string>();

/** Masks one line as it arrives and keeps the answer by `line`. */
export function noteChatLine(line: object, text: string): void {
  if (!masked.has(line)) masked.set(line, maskText(text, listWords()));
}

/** The text of one chat line as the reader sees it: masked while the
 *  filter is on. A line the state did not note is masked here, once. */
export function chatText(line: object, text: string, on: boolean): string {
  if (!on) return text;
  noteChatLine(line, text);
  return masked.get(line) ?? text;
}

/** True while the filter is on: the player's choice, or the platform's
 *  default where the player made none. */
export function chatFilterOn(s: Pick<GameSettings, 'chatFilter'>, app: StoreApp | null = storeApp()): boolean {
  return s.chatFilter === 'on' || (s.chatFilter === 'auto' && app !== null);
}

/**
 * The switch as the stored settings blob holds it, for a page that keeps
 * no settings object of its own: the hub clan chat card. The blob parses
 * again only when its text changed.
 */
export const storedChatFilterOn: () => boolean =
  storedReader(SETTINGS_KEY, (raw) => chatFilterOn(parseSettings(raw)));
